<template>
  <div
    v-if="renderedBlocks.length"
    ref="heroBanner"
    class="hero-banner container container--fluid"
  >
    <slideshow-carousel
      :autoplay="autoplay"
      :autoplay-speed="autoplaySpeed"
      :loop="loop"
    >
      <template
        v-for="(block, index) in renderedBlocks"
        :key="block.id"
        #[`slide-${index}`]
      >
        <div
          class="hero-banner__item banner-block"
          :class="{ 'banner-block--background': block.enable_background }"
          :style="block.css_variables"
        >
          <component
            :is="block.banner_url ? 'a' : 'div'"
            v-bind="block.banner_url ? { href: block.banner_url, 'aria-label': block.banner_url_title, class: 'banner-block__link' } : {}"
            class="hero-banner__item banner-block"
            :class="{ 'is-linked': block.banner_url }"
            :data-shopify-editor-block="shopifyAttributes[index].editorBlock"
          >
            <div class="banner-block__media">
              <media-element
                v-bind="getMediaElementProps(block)"
              />
            </div>
            <div class="banner-block__content content-align">
              <div class="banner-block__content-group">
                <heading
                  v-if="block.heading_text"
                  class="banner-block__heading"
                  :class="block.heading_alignment"
                  :type="block.heading_type"
                  v-html="block.heading_text"
                />

                <heading
                  v-if="block.subheading_text"
                  class="banner-block__subheading"
                  :class="block.subheading_alignment"
                  :type="block.subheading_type"
                  v-html="block.subheading_text"
                />

                <div
                  v-if="block.description_text"
                  class="banner-block__description"
                  :class="block.description_alignment"
                  v-html="block.description_text"
                />
                <div
                  class="banner-block__button-wrapper"
                  :class="block.button_alignment"
                >
                  <btn
                    v-if="block.button_text"
                    class="banner-block__button"
                    :class="block.button_type"
                    :label="block.button_text"
                    :url="!block.banner_url ? block.button_url : null"
                  />
                </div>
              </div>
            </div>
          </component>
        </div>
      </template>
    </slideshow-carousel>
  </div>
</template>

<script>

/**
 * Section: Hero
 * -----------------------------------------------------------------------------
 * - A customizable hero banner section designed to showcase prominent content
 * at the top of the page.
 * @param {String}  liquid.sectionId - Section's Liquid ID.
 * @param {Boolean} autoplay - Whether the carousel should autoplay. Default is true.
 * @param {Number}  autoplaySpeed - Interval in milliseconds for autoplay.
 * @param {Boolean} loop - Whether the carousel should loop back to the beginning after the last slide.
 * @param {Array}   blocks - Formatted section blocks array.
 */
import { mapState } from 'pinia'

import { useScreenStore } from '~stores/screen'

import { shopifyAttributesToJSON } from '~/helpers/strings'

import Btn from '~components/btn/btn'
import Heading from '~components/heading/heading'
import MediaElement from '~components/media-element/media-element'
import SlideshowCarousel from '~components/slideshow-carousel/slideshow-carousel'

export default {
  name: 'HeroBanner',

  components: {
    Btn,
    Heading,
    MediaElement,
    SlideshowCarousel,
  },

  props: {
    liquid: {
      type: Object,
      default: () => ({
        sectionId: '',
      }),
    },
    autoplay: {
      type: Boolean,
      default: true,
    },
    autoplaySpeed: {
      type: Number,
      default: 5000,
    },
    loop: {
      type: Boolean,
      default: true,
    },
    blocks: {
      type: Array,
      default: () => [],
    },
  },

  data() {
    return {
      renderedBlocks: [],
    }
  },

  computed: {

    /**
     * Map global state.
     */
    ...mapState(useScreenStore, {
      isMobile: 'mobile',
    }),

    /**
     * Converts {{ block.shopify_attributes }} to a usable equivalent attribute in JS.
     * @return {Array}
     */
    shopifyAttributes() {
      return this.blocks.map((block) => {
        return shopifyAttributesToJSON(block.shopify_attributes)
      })
    },
  },

  watch: {

    /**
     * Listen for if the device has been resized to mobile or desktop.
     */
    isMobile() {
      this.setFilteredBlocks(false)
    },
  },

  created() {
    this.setFilteredBlocks(true)
  },

  methods: {

    /**
     * Calculate which blocks to show based on current device
     * @param {Boolean} initialLoad - Indicates whether the blocks were rendered
     * for a first time.
     */
    setFilteredBlocks(initialLoad) {
      if (!this.blocks?.length) {
        return
      }

      const filteredBlocks = this.isMobile
        ? this.blocks.filter((block) => block.type?.includes('mobile'))
        : this.blocks.filter((block) => block.type?.includes('desktop'))

      this.renderedBlocks = filteredBlocks.map((block, index) => {

        /**
         * - This is done because desktop and mobile images `data-srcset` is
         * overwritten on resize
         * - The `lazySizes` library doesn't reflect that
         * - `lazySizes.init()` / `lazySizes.loader.unveil` for some reason
         *  don't trigger re-initialisation, so media-element shows wrong/old
         * sources
         * - Lazyload should always be `false` when image `data-src` changes
         * on the fly
         */
        const lazyload = Boolean(index !== 0 && initialLoad)

        return {
          ...block,
          lazyload,
        }
      })
    },

    /**
     * Get the properties for the media element.
     * Used to keep the template DRY when rendering a linked or non-linked media element.
     * @param {Object} block - The block object containing media properties.
     * @param {String} block.type - The type of the block.
     * @param {String} block.image - The image URL.
     * @param {String} block.image_desktop - The desktop image URL.
     * @param {String} block.video - The video URL.
     * @param {String} block.video_desktop - The desktop video URL.
     * @param {String} block.video_external_url - The external video URL.
     * @param {String} block.video_external_desktop_url - The external desktop video URL.
     * @param {Boolean} block.video_autoplay - Whether the video should autoplay.
     * @param {Boolean} block.video_loop - Whether the video should loop.
     * @param {Boolean} block.lazyload - Whether to lazyload the media.
     * @returns {Object} The properties for the media element.
     */
    getMediaElementProps(block) {
      return {
        class: `banner-block__media ${block.type}`,
        image: block.image,
        imageDesktop: block.image_desktop,
        video: block.video,
        videoDesktop: block.video_desktop,
        videoExternalUrl: block.video_external_url,
        videoExternalDesktopUrl: block.video_external_desktop_url,
        autoplayVideo: block.video_autoplay,
        loopVideo: block.video_loop,
        lazyload: block.lazyload,
      }
    },
  },
}
</script>

<style src="./hero-banner.scss" lang="scss"></style>
